/**
 * MIDI Clip View Component
 * Displays MIDI clips on the track timeline
 */
import { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { midiToNoteName } from './MIDIEngine';

export const MIDIClipView = ({
  clip,
  trackColor,
  pixelsPerSecond,
  totalDuration,
  isSelected,
  onSelect,
  onMove,
  onResize,
  onDoubleClick,
  onSplit,
  onJoinSelect,
  clipEditMode,
  isSelectedForJoin,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, startTime: 0 });
  const clipRef = useRef(null);
  
  // Use percentage-based positioning for consistent rendering
  const clipLeftPercent = (clip.startTime / totalDuration) * 100;
  const clipWidthPercent = (clip.duration / totalDuration) * 100;
  
  // Handle drag start
  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    
    // Handle split mode
    if (clipEditMode === 'split') {
      onSplit?.(clip.id);
      return;
    }
    
    // Handle join mode
    if (clipEditMode === 'join') {
      onJoinSelect?.(clip.id);
      return;
    }
    
    onSelect?.();
    
    const rect = clipRef.current.parentElement.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const containerWidth = rect.width;
    const clipLeftPx = (clipLeftPercent / 100) * containerWidth;
    const clipWidthPx = (clipWidthPercent / 100) * containerWidth;
    const clipRight = clipLeftPx + clipWidthPx;
    
    // Check if clicking on resize handle (right edge)
    if (clickX > clipRight - 10 && clickX <= clipRight) {
      setIsResizing(true);
      setDragStart({ x: e.clientX, duration: clip.duration, containerWidth });
    } else {
      setIsDragging(true);
      setDragStart({ x: e.clientX, startTime: clip.startTime, containerWidth });
    }
    
    // Add global listeners
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - e.clientX;
      const deltaTime = (deltaX / containerWidth) * totalDuration;
      
      if (isResizing || (clickX > clipRight - 10 && clickX <= clipRight)) {
        const newDuration = Math.max(0.5, clip.duration + deltaTime);
        onResize?.(clip.id, newDuration, false); // false = don't create undo yet
      } else {
        const newStartTime = Math.max(0, clip.startTime + deltaTime);
        onMove?.(clip.id, newStartTime, false); // false = don't create undo yet
      }
    };
    
    const handleMouseUp = (upEvent) => {
      const deltaX = upEvent.clientX - e.clientX;
      const deltaTime = (deltaX / containerWidth) * totalDuration;
      
      if (isResizing || (clickX > clipRight - 10 && clickX <= clipRight)) {
        const newDuration = Math.max(0.5, clip.duration + deltaTime);
        onResize?.(clip.id, newDuration, true); // true = create undo action
      } else if (Math.abs(deltaX) > 5) {
        const newStartTime = Math.max(0, clip.startTime + deltaTime);
        onMove?.(clip.id, newStartTime, true); // true = create undo action
      }
      
      setIsDragging(false);
      setIsResizing(false);
      
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [clip, clipLeftPercent, clipWidthPercent, totalDuration, onSelect, onMove, onResize, isResizing, clipEditMode, onSplit, onJoinSelect]);
  
  // Render mini piano roll preview
  const renderNotePreview = () => {
    if (!clip.notes || clip.notes.length === 0) return null;
    
    // Find pitch range
    const pitches = clip.notes.map(n => n.pitch);
    const minPitch = Math.min(...pitches);
    const maxPitch = Math.max(...pitches);
    const pitchRange = Math.max(12, maxPitch - minPitch + 1);
    
    const noteHeight = 48 / pitchRange; // 48px total height for notes
    
    return clip.notes.slice(0, 50).map((note, i) => {
      const noteLeft = (note.startTime / clip.duration) * 100;
      const noteWidth = Math.max(2, (note.duration / clip.duration) * 100);
      const noteTop = ((maxPitch - note.pitch) / pitchRange) * 100;
      
      return (
        <div
          key={note.id || i}
          className="absolute bg-white/60 rounded-sm"
          style={{
            left: `${noteLeft}%`,
            width: `${noteWidth}%`,
            top: `${noteTop}%`,
            height: `${Math.max(4, noteHeight)}px`,
          }}
        />
      );
    });
  };
  
  return (
    <motion.div
      ref={clipRef}
      className={`absolute top-1 bottom-1 rounded-lg overflow-hidden transition-all ${
        clipEditMode === 'split' ? 'cursor-crosshair' : 
        clipEditMode === 'join' ? 'cursor-pointer' : 'cursor-pointer'
      } ${
        isSelected 
          ? 'ring-2 ring-white shadow-lg' 
          : isSelectedForJoin
          ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-500/30'
          : 'hover:ring-1 hover:ring-white/50'
      } ${isDragging || isResizing ? 'opacity-80' : ''}`}
      style={{
        left: `${clipLeftPercent}%`,
        width: `${clipWidthPercent}%`,
        minWidth: '60px',
        background: isSelectedForJoin 
          ? `linear-gradient(135deg, rgba(16, 185, 129, 0.7), rgba(16, 185, 129, 0.4))`
          : `linear-gradient(135deg, ${trackColor || 'rgba(139, 92, 246, 0.6)'}, ${trackColor || 'rgba(139, 92, 246, 0.3)'})`,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={() => !clipEditMode && onDoubleClick?.(clip)}
      data-testid={`midi-clip-${clip.id}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      {/* Clip Header */}
      <div className="px-2 py-1 text-xs font-medium text-white/90 truncate bg-black/20">
        {clip.name}
        <span className="ml-2 text-white/60">{clip.notes?.length || 0} notes</span>
      </div>
      
      {/* Note Preview */}
      <div className="relative flex-1 h-12 mx-1 mb-1">
        {renderNotePreview()}
      </div>
      
      {/* Resize Handle */}
      <div 
        className="absolute top-0 bottom-0 right-0 w-2 cursor-ew-resize bg-white/0 hover:bg-white/20"
        title="Drag to resize"
      />
    </motion.div>
  );
};

export default MIDIClipView;
